import { addDays, differenceInDays, isWeekend, parseISO } from "date-fns";

/**
 * Jours fériés français (fixes et variables)
 * Note: Pour une vraie app, ces dates devraient être calculées dynamiquement
 * ou récupérées d'une API pour gérer les années futures
 */
export const getFrenchHolidays = (year: number): Date[] => {
  // Jours fériés fixes
  const fixedHolidays = [
    new Date(year, 0, 1),   // Jour de l'an
    new Date(year, 4, 1),   // Fête du travail
    new Date(year, 4, 8),   // Victoire 1945
    new Date(year, 6, 14),  // Fête nationale
    new Date(year, 7, 15),  // Assomption
    new Date(year, 10, 1),  // Toussaint
    new Date(year, 10, 11), // Armistice 1918
    new Date(year, 11, 25), // Noël
  ];

  // Pâques et jours fériés mobiles (calcul de Pâques selon algorithme de Meeus)
  const easter = calculateEaster(year);
  const mobileHolidays = [
    addDays(easter, 1),  // Lundi de Pâques
    addDays(easter, 39), // Ascension
    addDays(easter, 50), // Lundi de Pentecôte
  ];

  return [...fixedHolidays, ...mobileHolidays];
};

/**
 * Calcule la date de Pâques pour une année donnée (algorithme de Meeus)
 */
const calculateEaster = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month, day);
};

/**
 * Vérifie si une date est un jour férié français
 */
export const isFrenchHoliday = (date: Date): boolean => {
  const year = date.getFullYear();
  const holidays = getFrenchHolidays(year);
  return holidays.some(
    (holiday) =>
      holiday.getDate() === date.getDate() &&
      holiday.getMonth() === date.getMonth() &&
      holiday.getFullYear() === date.getFullYear()
  );
};

/**
 * Vérifie si une date est un jour ouvré (ni week-end ni férié)
 */
export const isWorkingDay = (date: Date): boolean => {
  return !isWeekend(date) && !isFrenchHoliday(date);
};

/**
 * Calcule le nombre de jours ouvrés entre deux dates (exclus week-ends et jours fériés)
 * @param startDate Date de début
 * @param endDate Date de fin (défaut: aujourd'hui)
 * @param absences Liste optionnelle de périodes d'absence à exclure
 */
export const calculateWorkingDays = (
  startDate: string | Date,
  endDate: string | Date = new Date(),
  absences: Array<{ date_debut: string; date_fin: string }> = []
): number => {
  const start = typeof startDate === "string" ? parseISO(startDate) : startDate;
  const end = typeof endDate === "string" ? parseISO(endDate) : endDate;

  if (start > end) return 0;

  let workingDays = 0;
  let currentDate = new Date(start);

  // Créer un Set des dates d'absence pour une recherche rapide
  const absenceDates = new Set<string>();
  absences.forEach(({ date_debut, date_fin }) => {
    const absStart = parseISO(date_debut);
    const absEnd = parseISO(date_fin);
    let absDate = new Date(absStart);
    
    while (absDate <= absEnd) {
      absenceDates.add(absDate.toISOString().split('T')[0]);
      absDate = addDays(absDate, 1);
    }
  });

  while (currentDate <= end) {
    const dateString = currentDate.toISOString().split('T')[0];
    
    // Vérifier si c'est un jour ouvré ET pas une absence
    if (isWorkingDay(currentDate) && !absenceDates.has(dateString)) {
      workingDays++;
    }
    
    currentDate = addDays(currentDate, 1);
  }

  return workingDays;
};

/**
 * Calcule le nombre total de jours (calendaires) entre deux dates
 */
export const calculateCalendarDays = (
  startDate: string | Date,
  endDate: string | Date = new Date()
): number => {
  const start = typeof startDate === "string" ? parseISO(startDate) : startDate;
  const end = typeof endDate === "string" ? parseISO(endDate) : endDate;
  
  return Math.max(0, differenceInDays(end, start));
};

/**
 * Estime la date de fin d'un projet basée sur les jours ouvrés restants
 */
export const estimateEndDate = (
  startDate: string | Date,
  workingDaysNeeded: number
): Date => {
  const start = typeof startDate === "string" ? parseISO(startDate) : startDate;
  let currentDate = new Date(start);
  let workingDaysCount = 0;

  while (workingDaysCount < workingDaysNeeded) {
    currentDate = addDays(currentDate, 1);
    if (isWorkingDay(currentDate)) {
      workingDaysCount++;
    }
  }

  return currentDate;
};

// ============================================================================
// DATE DE FIN EFFECTIVE - Source unique de vérité
// ============================================================================

/**
 * Détail d'une demi-journée travaillée
 */
export interface JourDetail {
  date: string;
  period: 'AM' | 'PM';
  value: number;
}

/**
 * Affectation retard (données nécessaires pour le calcul)
 */
export interface AffectationRetard {
  date_fin: string;
  jours_travailles: number;
  jours_details?: JourDetail[] | null;
}

/**
 * Suspension (données nécessaires pour le calcul)
 */
export interface SuspensionInfo {
  date_suspension: string;
  date_reprise: string | null;
  duree_suspension_jours: number | null;
}

/**
 * Paramètres pour le calcul de la date de fin effective
 */
export interface GetDateFinEffectiveParams {
  date_fin_estimee: string | null;
  date_fin_initiale?: string | null;
  affectationsRetard?: AffectationRetard[];
  suspensionsTerminees?: SuspensionInfo[];
}

/**
 * Ajoute N jours ouvrés à une date (exclut weekends et jours fériés)
 */
export const addWorkingDays = (date: Date, days: number): Date => {
  if (days <= 0) return date;

  let currentDate = new Date(date);
  let addedDays = 0;

  while (addedDays < days) {
    currentDate = addDays(currentDate, 1);
    if (isWorkingDay(currentDate)) {
      addedDays++;
    }
  }

  return currentDate;
};

/**
 * Calcule la date de fin effective d'un chantier.
 *
 * Logique :
 * 1. Part de date_fin_initiale (ou date_fin_estimee si pas de retards)
 * 2. Ajoute les jours de retard (somme de jours_travailles des affectations retard)
 * 3. Ajoute les jours de suspension terminées (somme de duree_suspension_jours)
 * 4. Utilise Math.round() pour les demi-journées :
 *    - 0.5 → 1 jour (même jour calendaire, fin de matinée)
 *    - 1.0 → 1 jour
 *    - 1.5 → 2 jours
 * 5. Retourne la date calculée (ou date_fin_estimee si > calculée)
 *
 * @returns Date de fin effective (ou null si pas de date estimée)
 */
export function getDateFinEffective({
  date_fin_estimee,
  date_fin_initiale,
  affectationsRetard = [],
  suspensionsTerminees = []
}: GetDateFinEffectiveParams): Date | null {
  // Pas de date estimée → null
  if (!date_fin_estimee) return null;

  // Date de base : initiale si existe, sinon estimée
  const dateBase = date_fin_initiale
    ? parseISO(date_fin_initiale)
    : parseISO(date_fin_estimee);

  // Calculer total jours de retard en DÉDUPLICANT via jours_details
  // (un même jour travaillé par 3 membres = 1 seul jour de retard, pas 3)
  const uniqueDelaySlots = new Set<string>();
  let hasJoursDetails = false;

  for (const aff of affectationsRetard) {
    if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
      hasJoursDetails = true;
      for (const detail of aff.jours_details) {
        uniqueDelaySlots.add(`${detail.date}:${detail.period}`);
      }
    }
  }

  let totalJoursRetard: number;
  if (hasJoursDetails) {
    totalJoursRetard = 0;
    for (const key of uniqueDelaySlots) {
      const period = key.split(':')[1];
      totalJoursRetard += period === 'FULL' ? 1 : 0.5;
    }
  } else {
    // Fallback sans jours_details : prendre le MAX (pas la somme !)
    // car plusieurs affectations pour le même retard = même période
    totalJoursRetard = affectationsRetard.length > 0
      ? Math.max(...affectationsRetard.map(a => a.jours_travailles || 0))
      : 0;
  }

  // Calculer total jours de suspension (seulement suspensions terminées avec date_reprise)
  const totalJoursSuspension = suspensionsTerminees
    .filter(s => s.date_reprise !== null)
    .reduce((sum, s) => sum + (s.duree_suspension_jours || 0), 0);

  // Total jours à ajouter
  const totalJoursAjoutes = totalJoursRetard + totalJoursSuspension;

  // Si aucun jour à ajouter, retourner date_fin_estimee
  if (totalJoursAjoutes === 0) {
    return parseISO(date_fin_estimee);
  }

  // Arrondir pour les demi-journées (0.5 → 1, 1.5 → 2)
  const joursOuvresAAjouter = Math.round(totalJoursAjoutes);

  // Calculer nouvelle date (jours ouvrés, exclut weekends et fériés)
  const dateCalculee = addWorkingDays(dateBase, joursOuvresAAjouter);

  // Retourner la plus tardive entre calculée et estimée actuelle
  // (au cas où l'utilisateur a manuellement ajusté la date)
  const dateEstimee = parseISO(date_fin_estimee);

  return dateCalculee > dateEstimee ? dateCalculee : dateEstimee;
}

/**
 * Résultat du calcul de date effective avec métadonnées
 */
export interface DateFinEffectiveResult {
  dateEffective: Date | null;
  totalJoursRetard: number;
  totalJoursSuspension: number;
  totalJoursAjoutes: number;
  aEteDecalee: boolean;
}
